import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, TextInput, RefreshControl, ImageBackground, Platform, Alert, Keyboard } from 'react-native';
import SwipeableTabs, { SwipeableTabsRef } from '@/components/SwipeableTabs';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import * as Haptics from 'expo-haptics';
import { useSharedValue, withRepeat, withTiming, Easing, cancelAnimation } from 'react-native-reanimated';
import FilterDrawer from '@/components/FilterDrawer';
import TimeFilterContent from '@/components/TimeFilterContent';
import DayFilterContent from '@/components/DayFilterContent';
import CategoryFilterContent, { type CategoryOption } from '@/components/CategoryFilterContent';
import FilterChip, { ClockChipIcon, CalendarChipIcon, CategoryChipIcon } from '@/components/FilterChip';
import { translateCategory } from '@/utils/meetingLocale';
import Toast from '@/components/Toast';
import BottomSheet from '@gorhom/bottom-sheet';
import { Meeting, UserMeetingPreferences, PROFILE_MEETING_TYPES } from '@/types/meetings';
import { MeetingsService } from '@/services/meetingsService';
import { SavedMeetingsService } from '@/services/savedMeetingsService';
import { CheckinService } from '@/services/checkinService';
import meetingStatsService from '@/services/meetingStatsService';
import onboardingService from '@/services/onboardingService';
import meetingPreferencesService from '@/services/meetingPreferencesService';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getMeetingDisplayTime } from '@/utils/meetingUtils';
import FloatingActionButton from '@/components/FloatingActionButton';
import BillBottomNav from '@/components/BillBottomNav';
import { SkeletonMeetingCard } from '@/components/Skeleton';
import EmptyState from '@/components/EmptyState';
import Avatar from '@/components/Avatar';
import AvatarBlurRing from '@/components/AvatarBlurRing';
import { useServerVerifiedPlus } from '@/hooks/useEntitlement';
import { LinearGradient } from 'expo-linear-gradient';
import { MeetingGroup } from '@/types/groups';
import { onClinicReady } from '@/services/meetingsService';
import { translateMeetings, translateGroups } from '@/utils/meetingTranslation';
import { env } from '@/config/environment';
import CarouselPagination from '@/components/CarouselPagination';
import MegaphoneIcon from '@/components/icons/MegaphoneIcon';
import StarsIcon from '@/components/icons/StarsIcon';
import SearchOverlay from '@/components/SearchOverlay';

// ── Plus / Padrinho Plus visual indicators ────────────────────────────────────
const GemIconStroke = ({ size = 16, color = '#669AB7' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 12 12" fill="none">
    <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
    <Path d="M1.50391 5.10156H10.4954" stroke={color} strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// "Shaped-square" 32x32 button used on carousel + group section cards
const ShapedSquareDiamond = () => (
  <View style={{ backgroundColor: '#F6F7F9', borderRadius: 12, width: 32, height: 32, justifyContent: 'center', alignItems: 'center' }}>
    <GemIconStroke size={16} color="#669AB7" />
  </View>
);

// Blue corner overlay used on the meetings list thumbnails (mirrors favorite badge)
const MeetingPlusBadge = () => (
  <View style={{ position: 'absolute', top: 0, left: 0, width: 32, height: 32 }}>
    <View style={{ position: 'absolute', top: 0, left: 0, width: 24, height: 24, backgroundColor: '#669AB7', borderBottomRightRadius: 12, justifyContent: 'center', alignItems: 'center' }}>
      <GemIconStroke size={14} color="#FFFFFF" />
    </View>
    <Svg style={{ position: 'absolute', top: 0, left: 24 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
      <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#669AB7" />
    </Svg>
    <Svg style={{ position: 'absolute', top: 24, left: 0 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
      <Path d="M0 0V8C0 1.29032 2.83871 0 8 0H0Z" fill="#669AB7" />
    </Svg>
  </View>
);

const ADDICTION_GROUP_SLUGS: Record<string, string[]> = {
  alcohol: ['aa'],
  drugs: ['na'],
  nicotine: ['na'],
  gambling: ['ja'],
  sex: ['sa'],
  pornography: ['sa'],
  other: ['outros', 'other'],
};

const ADDICTION_GROUP_KEYWORDS: Record<string, string[]> = {
  alcohol: ['alcoólicos', 'alcohlicos', 'alcoholicos', 'alcoholics'],
  drugs: ['narcóticos', 'narcoticos', 'narcotics'],
  nicotine: ['narcóticos', 'narcoticos', 'narcotics'],
  gambling: ['jogadores', 'gamblers'],
  sex: ['sexólatras', 'sexolatras', 'sexaholics'],
  pornography: ['sexólatras', 'sexolatras', 'sexaholics'],
  other: [],
};

function findGroupForAddiction(
  groups: import('@/types/groups').MeetingGroup[],
  primaryAddiction: string | null
): import('@/types/groups').MeetingGroup | null {
  if (!primaryAddiction || groups.length === 0) return null;
  const slugs = ADDICTION_GROUP_SLUGS[primaryAddiction] ?? [];
  const keywords = ADDICTION_GROUP_KEYWORDS[primaryAddiction] ?? [];
  if (slugs.length === 0 && keywords.length === 0) return null;
  return groups.find(g => {
    const slug = g.slug?.toLowerCase() ?? '';
    const name = g.name?.toLowerCase() ?? '';
    return slugs.some(s => slug === s) || keywords.some(k => name.includes(k));
  }) ?? null;
}

export default function EncontrosOnlineScreen() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { selectedMeetingId } = useLocalSearchParams<{ selectedMeetingId?: string }>();
  const { user, userProfile, sessionReady, session } = useAuth();
  const { unreadCount, markAllAsRead } = useNotifications();
  const { hasPadrinhoPlus } = useServerVerifiedPlus();
  const [activeTab, setActiveTab] = useState('explore');

  useEffect(() => {
    // PRD §5.6 — `meetings_list_viewed`. Fire once on screen mount with
    // the initial tab. Tab-switch events are not separately tagged because
    // Clarity already records page-time and the dashboard cares about
    // total list-screen volume per tab. NOTE: PRD spec said the tab enum
    // would be `para_voce | todas | salvos`, but the shipping UI only has
    // `para_voce | explore` — see PRD §5.6 discrepancy note.
    import('@/utils/clarityEvents')
      .then(({ logMeetingListViewed }) =>
        logMeetingListViewed({ tab: 'explore' })
      )
      .catch(() => {});
  }, []);
  const meetingsSwipeRef = useRef<SwipeableTabsRef>(null);
  // PRD §7.3 — Salvos tab restored as the third tab. Order matters for
  // SwipeableTabs index mapping (left → right).
  const MEETING_TABS = ['explore', 'para_voce'] as const;
  const meetingTabToIndex = (tab: string) => MEETING_TABS.indexOf(tab as any);

  // PRD §5.6 — Centralised tab-change handler. Both tap (`handleMeetingTabPress`)
  // and swipe (`onPageSelected`) funnel through here so `salvos_tab_viewed`
  // fires exactly once per entry, regardless of how the user got there.
  const changeMeetingTab = (tab: string, source: 'tap' | 'swipe') => {
    if (activeTab === tab) return;
    if (source === 'tap') Haptics.selectionAsync();
    setActiveTab(tab);
    if (tab === 'salvos') {
      import('@/utils/clarityEvents')
        .then(({ logUiAction }) =>
          logUiAction({
            surface: 'meetings_list',
            element: 'salvos_tab',
            action: 'viewed',
            variant: source,
          })
        )
        .catch(() => {});
    }
  };

  const handleMeetingTabPress = (tab: string) => changeMeetingTab(tab, 'tap');

  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [tempSelectedTimes, setTempSelectedTimes] = useState<string[]>([]);
  const [tempSelectedDays, setTempSelectedDays] = useState<string[]>([]);
  const [tempSelectedCategories, setTempSelectedCategories] = useState<string[]>([]);
  const [timeFilterVisible, setTimeFilterVisible] = useState(false);
  const [dayFilterVisible, setDayFilterVisible] = useState(false);
  const [categoryFilterVisible, setCategoryFilterVisible] = useState(false);
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [savedMeetings, setSavedMeetings] = useState<string[]>([]);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedMeetingsLoading, setSavedMeetingsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [groupsWithMeetings, setGroupsWithMeetings] = useState<MeetingGroup[]>([]);
  const [meetingAttendees, setMeetingAttendees] = useState<Record<string, { userId: string; profilePhotoUrl: string | null }[]>>({});
  const [testNotificationLoading, setTestNotificationLoading] = useState(false);
  const [meetingPreferences, setMeetingPreferences] = useState<UserMeetingPreferences | null>(null);
  const [paraVoceMeetings, setParaVoceMeetings] = useState<Meeting[]>([]);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [paraVoceCarouselIndex, setParaVoceCarouselIndex] = useState(0);
  const rotation = useSharedValue(0);
  const timeFilterRef = useRef<BottomSheet>(null);
  const dayFilterRef = useRef<BottomSheet>(null);
  const categoryFilterRef = useRef<BottomSheet>(null);

  const sendTestNotification = async () => {
    if (!user) {
      showToast('Please log in first', 'error');
      return;
    }

    try {
      setTestNotificationLoading(true);
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);

      console.log('🔔 Sending test notification to user:', user.id);
      console.log('🔔 API URL:', env.apiUrl);

      const response = await fetch(`${env.apiUrl}/api/notifications/test-push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          title: '🔔 Test Meeting Reminder',
          message: 'Your AA meeting "Serenity Group" starts in 10 minutes!',
        }),
      });

      const responseText = await response.text();
      console.log('🔔 Response status:', response.status);
      console.log('🔔 Response text:', responseText.substring(0, 200));

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Failed to parse response as JSON:', responseText.substring(0, 500));
        showToast('Server error - try again', 'error');
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
        return;
      }

      if (result.success) {
        showToast('Test notification sent! Check your device.', 'success');
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      } else {
        showToast(`Failed: ${result.error || 'Unknown error'}`, 'error');
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
      }
    } catch (error: any) {
      console.error('❌ Test notification error:', error);
      showToast(`Error: ${error.message}`, 'error');
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      setTestNotificationLoading(false);
    }
  };

  // Pull to refresh handler
  const onRefresh = async () => {
    setRefreshing(true);
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    
    // Start rotation animation
    rotation.value = withRepeat(
      withTiming(360, {
        duration: 1000,
        easing: Easing.linear,
      }),
      -1, // infinite repeat
      false
    );

    // Refresh meetings data
    try {
      const meetingsData = await MeetingsService.getAllMeetings();
      
      // Translate meetings based on user language
      const languageMap: Record<string, string> = { 'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES' };
      const targetLanguage = languageMap[language] || 'pt-BR';
      const translatedMeetings = await translateMeetings(meetingsData, targetLanguage);
      
      setMeetings(translatedMeetings);
      
      // Get unique group IDs that have meetings
      const groupIds = [...new Set(meetingsData.map(m => m.group_id).filter(Boolean))];
      
      // Fetch groups data via API (bypasses Supabase timing issues)
      if (groupIds.length > 0) {
        const groupsData = await MeetingsService.getMeetingGroups(groupIds);
        if (groupsData.length > 0) {
          // Translate groups based on user language
          const translatedGroups = await translateGroups(groupsData, targetLanguage);
          setGroupsWithMeetings(translatedGroups);
        }
      } else {
        setGroupsWithMeetings([]);
      }
      
      if (user) {
        const savedIds = await SavedMeetingsService.getSavedMeetings(user.id);
        setSavedMeetings(savedIds);
      }
      
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
    } catch (error) {
      console.error('Error refreshing meetings:', error);
      Haptics.notificationAsync(Haptics.NotificationFeedbackType.Error);
    } finally {
      cancelAnimation(rotation);
      rotation.value = withTiming(0, { duration: 200 });
      setRefreshing(false);
    }
  };

  // CRITICAL FIX: Load meetings data and groups when session is ready
  // This ensures the Supabase client is fully initialized before making queries
  useEffect(() => {
    let cancelled = false;
    // Last-write-wins guard: the silent refetch (fired by onClinicReady) can
    // race the initial load. Only the most recent invocation may commit state.
    let invocationCounter = 0;
    let latestInvocation = 0;

    const loadMeetingsAndGroups = async (opts?: { silent?: boolean }) => {
      // Wait for session to be ready - even for public data, Supabase client needs to be initialized
      if (!sessionReady) {
        console.log('MeetingsScreen: Waiting for session ready before loading meetings...');
        return;
      }

      const myInvocation = ++invocationCounter;
      latestInvocation = myInvocation;
      const isStale = () => cancelled || myInvocation !== latestInvocation;

      try {
        if (!opts?.silent) setLoading(true);
        console.log('MeetingsScreen: Loading meetings data via API...');
        const meetingsData = await MeetingsService.getAllMeetings();
        if (isStale()) return;
        console.log('MeetingsScreen: Loaded', meetingsData.length, 'meetings');

        // Translate meetings based on user language
        const languageMap: Record<string, string> = { 'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES' };
        const targetLanguage = languageMap[language] || 'pt-BR';
        const translatedMeetings = await translateMeetings(meetingsData, targetLanguage);
        if (isStale()) return;

        setMeetings(translatedMeetings);

        // Get unique group IDs that have meetings
        const groupIds = [...new Set(meetingsData.map(m => m.group_id).filter(Boolean))];

        // Fetch groups data via API (bypasses Supabase timing issues)
        if (groupIds.length > 0) {
          const groupsData = await MeetingsService.getMeetingGroups(groupIds);
          if (isStale()) return;
          if (groupsData.length > 0) {
            // Translate groups based on user language
            const translatedGroups = await translateGroups(groupsData, targetLanguage);
            if (isStale()) return;
            setGroupsWithMeetings(translatedGroups);
          }
        } else {
          setGroupsWithMeetings([]);
        }

        // If redirected from menu with a specific meeting, navigate to its page
        if (selectedMeetingId && meetingsData.length > 0) {
          const selectedMeeting = meetingsData.find(meeting => meeting.id === selectedMeetingId);
          if (selectedMeeting) {
            router.push(`/meeting/${selectedMeeting.id}`);
          }
        }
      } catch (error) {
        console.error('Error loading meetings and groups:', error);
        if (!opts?.silent) showToast(t('meetings.errorLoading'), 'error');
      } finally {
        if (!opts?.silent && !cancelled) setLoading(false);
      }
    };

    loadMeetingsAndGroups();

    // Once the user's clinic_id resolves, refetch silently to merge in
    // clinic-managed (Elo) meetings and groups. The first load fires before
    // AuthContext warms up, so without this the Elo merge would never apply
    // on cold start.
    const unsubscribe = onClinicReady(() => {
      console.log('MeetingsScreen: Clinic ready — refetching to merge clinic-managed meetings');
      loadMeetingsAndGroups({ silent: true });
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // C3 (PV2-16): access_token rotates on auto-refresh; sessionReady is enough.
  }, [language, sessionReady]);

  // CRITICAL FIX: Load saved meetings when user AND sessionReady are available
  // This ensures the auth token is available in apiService before making API calls
  useEffect(() => {
    const loadSavedMeetings = async () => {
      if (!user || !sessionReady || !session?.access_token) {
        if (user && !sessionReady) {
          console.log('MeetingsScreen: Waiting for session validation...');
        }
        setSavedMeetingsLoading(false);
        return;
      }

      try {
        setSavedMeetingsLoading(true);
        const savedMeetingIds = await SavedMeetingsService.getSavedMeetings(user.id);
        setSavedMeetings(savedMeetingIds);
      } catch (error) {
        console.error('Error loading saved meetings:', error);
        showToast(t('meetings.errorLoadingSaved'), 'error');
      } finally {
        setSavedMeetingsLoading(false);
      }
    };

    loadSavedMeetings();
    // C3 (PV2-16): access_token rotation must not retrigger this effect.
  }, [user, sessionReady]);

  // Check meeting onboarding completion — redirect to onboarding flow if not done
  useEffect(() => {
    const checkOnboarding = async () => {
      if (!user || !sessionReady || !session?.access_token || onboardingChecked) return;

      try {
        const status = await onboardingService.getStatus();
        setOnboardingChecked(true);

        if (!status.meeting_onboarding) {
          // Push (not replace) so the meetings hub stays in the stack. That
          // way the onboarding's back button pops smoothly back here (the
          // previous page) instead of jumping past it to the home tab.
          router.push('/encontros-onboarding');
          return;
        }

        const prefs = await meetingPreferencesService.getPreferences();
        setMeetingPreferences(prefs);
      } catch (error) {
        console.error('[MeetingsScreen] Onboarding check error:', error);
        setOnboardingChecked(true);
      }
    };

    checkOnboarding();
    // C3 (PV2-16): access_token rotation must not retrigger onboarding check.
  }, [user, sessionReady, onboardingChecked]);

  // Recompute suggested meetings whenever the catalogue or user preferences change.
  // PRD §6 — prefer the server-ranked /api/meetings/suggestions endpoint (15-min
  // server cache, two-layer profile→type / addiction→group ranking, BR-local
  // next-session ordering). Falls back to onboarding-pinned IDs → local
  // profile→type filter when the API is unreachable, so the tab is never empty.
  useEffect(() => {
    const derivedProfile = meetingPreferences?.derived_profile;
    if (!derivedProfile || meetings.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const suggested = await MeetingsService.getSuggestions(10);
        if (cancelled) return;
        if (suggested && suggested.length > 0) {
          setParaVoceMeetings(suggested);
          return;
        }
      } catch {
        // fall through to legacy path
      }
      if (cancelled) return;
      const suggestedIds = meetingPreferencesService.getSuggestedMeetingIds();
      if (suggestedIds && suggestedIds.length > 0) {
        const ordered = suggestedIds
          .map((id) => meetings.find((m) => m.id === id))
          .filter((m): m is Meeting => m !== undefined);
        if (ordered.length > 0) {
          setParaVoceMeetings(ordered);
          return;
        }
      }
      const recommendedTypes = PROFILE_MEETING_TYPES[derivedProfile] ?? [];
      const matched = meetings
        .filter((m) => m.meeting_type && recommendedTypes.includes(m.meeting_type))
        .slice(0, 10);
      setParaVoceMeetings(matched);
    })();
    return () => { cancelled = true; };
  }, [meetings, meetingPreferences]);

  // Load recent attendees for all meetings using global stats API (bypasses RLS)
  useEffect(() => {
    const loadMeetingAttendees = async () => {
      if (meetings.length === 0) return;

      try {
        // Use batch API to fetch global stats for all meetings at once
        const meetingIds = meetings.map(m => m.id);
        const batchStats = await meetingStatsService.getBatchMeetingStats(meetingIds);
        
        // Transform batch stats to the expected format
        const attendeesMap: Record<string, { userId: string; profilePhotoUrl: string | null }[]> = {};
        for (const meetingId of meetingIds) {
          attendeesMap[meetingId] = batchStats[meetingId]?.recentAttendees || [];
        }
        
        setMeetingAttendees(attendeesMap);
        console.log('✅ Loaded global meeting attendees for', meetingIds.length, 'meetings');
      } catch (error) {
        console.error('Error loading meeting attendees:', error);
        // Fallback to empty for all meetings
        const attendeesMap: Record<string, { userId: string; profilePhotoUrl: string | null }[]> = {};
        meetings.forEach(m => { attendeesMap[m.id] = []; });
        setMeetingAttendees(attendeesMap);
      }
    };

    loadMeetingAttendees();
  }, [meetings]);

  // Initialize search presets with translated values
  useEffect(() => {
    setRecentSearches([
      t('meetings.search.preset1'),
      t('meetings.search.preset2')
    ]);
  }, [language]);

  const handleTimeFilter = () => {
    Keyboard.dismiss();
    setTempSelectedTimes(selectedTimes);
    setTimeFilterVisible(true);
  };

  const handleDayFilter = () => {
    Keyboard.dismiss();
    setTempSelectedDays(selectedDays);
    setDayFilterVisible(true);
  };

  const handleCategoryFilter = () => {
    Keyboard.dismiss();
    setTempSelectedCategories(selectedCategories);
    setCategoryFilterVisible(true);
  };

  const handleTimeFilterApply = () => {
    setSelectedTimes(tempSelectedTimes);
    setTimeFilterVisible(false);
  };

  const handleDayFilterApply = () => {
    setSelectedDays(tempSelectedDays);
    setDayFilterVisible(false);
  };

  const handleCategoryFilterApply = () => {
    setSelectedCategories(tempSelectedCategories);
    setCategoryFilterVisible(false);
  };

  const removeTimeFilter = () => {
    setSelectedTimes([]);
  };

  const removeDayFilter = () => {
    setSelectedDays([]);
  };

  const removeCategoryFilter = () => {
    setSelectedCategories([]);
  };

  const clearAllFilters = () => {
    setSelectedTimes([]);
    setSelectedDays([]);
    setSelectedCategories([]);
  };

    const handleTimeFilterClear = () => {
        setTempSelectedTimes([]);
    };

    const handleDayFilterClear = () => {
        setTempSelectedDays([]);
    };

    const handleCategoryFilterClear = () => {
        setTempSelectedCategories([]);
    };

  const handleSearchPress = () => {
    setIsSearchActive(true);
  };

  const handleSearchCancel = () => {
    setSearchQuery('');
    setIsSearchActive(false);
    // Also drop any filters that were applied inside the search overlay so
    // the list returns to its default state when the user backs out.
    setSelectedTimes([]);
    setSelectedDays([]);
    setSelectedCategories([]);
    setTempSelectedTimes([]);
    setTempSelectedDays([]);
    setTempSelectedCategories([]);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim() && !recentSearches.includes(searchQuery.trim())) {
      setRecentSearches([searchQuery.trim(), ...recentSearches.slice(0, 4)]);
    }
  };

  const handleRecentSearchPress = (search: string) => {
    setSearchQuery(search);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  const hideToast = () => {
    setToastVisible(false);
  };

  const toggleSavedMeeting = async (meetingId: string) => {
    if (!user) {
      showToast(t('meetings.loginToSave'), 'error');
      return;
    }

    // Optimistic update - change UI immediately
    const wasSaved = savedMeetings.includes(meetingId);
    const newIsSaved = !wasSaved;
    
    setSavedMeetings(prev => {
      if (newIsSaved) {
        return [...prev, meetingId];
      } else {
        return prev.filter(id => id !== meetingId);
      }
    });

    // Perform the save operation in the background
    try {
      const result = await SavedMeetingsService.toggleSavedMeeting(user.id, meetingId);

      if (result.success) {
        if (result.isSaved) {
          showToast(t('meetings.saved_success'), 'success');
        }
      } else {
        // Revert optimistic update on failure
        setSavedMeetings(prev => {
          if (wasSaved) {
            return [...prev, meetingId];
          } else {
            return prev.filter(id => id !== meetingId);
          }
        });
        showToast(t('meetings.errorUpdate'), 'error');
      }
    } catch (error) {
      console.error('Error toggling saved meeting:', error);
      // Revert optimistic update on error
      setSavedMeetings(prev => {
        if (wasSaved) {
          return [...prev, meetingId];
        } else {
          return prev.filter(id => id !== meetingId);
        }
      });
      showToast('Erro ao atualizar encontro salvo', 'error');
    }
  };


  const handleMeetingPress = (meeting: Meeting, source?: 'para_voce' | 'explore' | 'salvos') => {
    if (source === 'para_voce') {
      // PRD §5.6 — fire ONLY for Para Você cards so dashboard funnel reflects
      // the suggestion engine's hit rate, not all meeting taps.
      import('@/utils/clarityEvents')
        .then(({ logClarityEvent }) =>
          logClarityEvent('suggestion_card_tapped', {
            surface: 'meetings_list',
            tab: 'para_voce',
            meeting_id: meeting.id,
            meeting_type: meeting.meeting_type,
          }),
        )
        .catch(() => {});
    }
    router.push(`/meeting/${meeting.id}`);
  };

  // Build a normalized sessions list per meeting. Post-P12 schema split the
  // meeting catalog into `meetings` (parent) + `meetings_sessions` (one row
  // per weekday+time). The list endpoint embeds sessions[]; older payloads
  // (or rows still on the flat shape) fall back to the parent's flat
  // weekday/time/category columns so filtering keeps working on both.
  // Distinct meeting_category values present across every session of every
  // loaded meeting. Drives the Categoria filter so the list mirrors what is
  // actually selectable in the current catalog (instead of a hardcoded
  // taxonomy that might not match what's stored in meetings_sessions).
  const sessionCategoryOptions: CategoryOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const result: CategoryOption[] = [];
    for (const m of meetings) {
      const embedded = (m as any).sessions;
      // ONLY pull from meetings_sessions — the legacy parent
      // meetings.meeting_category column is intentionally ignored so the
      // filter mirrors the session-level taxonomy.
      if (!Array.isArray(embedded)) continue;
      for (const s of embedded) {
        // The /api/meetings list endpoint maps session rows to
        // { id, weekday, time, period, category } — so read `category`
        // here, not `meeting_category`.
        const raw = String(s?.category || s?.meeting_category || '').trim();
        if (!raw || seen.has(raw)) continue;
        seen.add(raw);
        result.push({ key: raw, label: translateCategory(raw, t) });
      }
    }
    result.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return result;
  }, [meetings, t]);

  const getSessionsToCheck = (meeting: Meeting) => {
    const embedded = (meeting as any).sessions;
    if (Array.isArray(embedded) && embedded.length > 0) {
      // List endpoint shape: { id, weekday, time, period, category }.
      // Older shapes use meeting_time / meeting_category — accept both.
      return embedded.map((s: any) => ({
        weekday: String(s.weekday || ''),
        meeting_time: String(s.time ?? s.meeting_time ?? ''),
        meeting_category: String(s.category ?? s.meeting_category ?? ''),
      }));
    }
    // Flat fallback: explode the multi-weekday string into one pseudo
    // session per day so the day filter still hits every weekday a meeting
    // runs on.
    const days = (meeting.meeting_weekday || '')
      .split(/[;,]/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (days.length === 0) {
      return [{
        weekday: '',
        meeting_time: meeting.meeting_time || '',
        meeting_category: meeting.meeting_category || '',
      }];
    }
    return days.map((d) => ({
      weekday: d,
      meeting_time: meeting.meeting_time || '',
      meeting_category: meeting.meeting_category || '',
    }));
  };

  // Shared filter predicate used by both carousel + list views. Search
  // matches name/category/place/region; time/day match against ANY session;
  // category matches a normalized session category.
  const matchesFilters = (meeting: Meeting) => {
    if (searchQuery.length >= 3) {
      const q = searchQuery.toLowerCase();
      const searchMatch = meeting.meeting_name.toLowerCase().includes(q) ||
        (!!meeting.meeting_category && meeting.meeting_category.toLowerCase().includes(q)) ||
        meeting.meeting_place.toLowerCase().includes(q) ||
        (!!meeting.meeting_region && meeting.meeting_region.toLowerCase().includes(q));
      if (!searchMatch) return false;
    }

    const sessions = getSessionsToCheck(meeting);

    if (selectedTimes.length > 0) {
      const timeMatch = sessions.some((s) => selectedTimes.includes(s.meeting_time));
      if (!timeMatch) return false;
    }

    if (selectedDays.length > 0) {
      const dayMatch = selectedDays.some((selectedDay) => {
        const needle = selectedDay.toLowerCase();
        return sessions.some((s) => (s.weekday || '').toLowerCase().includes(needle));
      });
      if (!dayMatch) return false;
    }

    if (selectedCategories.length > 0) {
      const catMatch = sessions.some((s) =>
        selectedCategories.includes(String(s.meeting_category || ''))
      );
      if (!catMatch) return false;
    }

    return true;
  };

  // Carousel meetings - always show all meetings (no tab filter), only apply search/time/day/category filters
  const carouselMeetings = meetings.filter(matchesFilters);

  // List meetings - apply search/time/day/category filters
  const filteredMeetings = meetings.filter((meeting) => {
    if (!matchesFilters(meeting)) return false;

    return true;
  }).sort((a, b) => {
    // Check if meetings are live using getMeetingDisplayTime (checks BOTH time AND weekday)
    const isLiveA = getMeetingDisplayTime(a.meeting_time, a.meeting_weekday, t).isLive;
    const isLiveB = getMeetingDisplayTime(b.meeting_time, b.meeting_weekday, t).isLive;
    
    // Prioritize live meetings first
    if (isLiveA && !isLiveB) return -1;
    if (!isLiveA && isLiveB) return 1;
    
    // If both are live or both are not live, sort by proximity to current time
    const now = new Date();
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    
    const getMeetingTimeInMinutes = (meetingTime: string): number => {
      const [hours, minutes] = meetingTime.split(':').map(Number);
      return (hours || 0) * 60 + (minutes || 0);
    };
    
    const timeA = getMeetingTimeInMinutes(a.meeting_time);
    const timeB = getMeetingTimeInMinutes(b.meeting_time);
    
    // Calculate time difference from current time
    // For meetings that haven't started yet today, use positive difference
    // For meetings that have passed today, add 24 hours (next day)
    const getDifferenceFromNow = (meetingTimeMinutes: number): number => {
      let difference = meetingTimeMinutes - currentTimeInMinutes;
      if (difference < 0) {
        difference += 24 * 60; // Add 24 hours for next occurrence
      }
      return difference;
    };
    
    const diffA = getDifferenceFromNow(timeA);
    const diffB = getDifferenceFromNow(timeB);
    
    // Sort by closest upcoming time
    return diffA - diffB;
  });

  const MeetingCard = ({ meeting }: { meeting: Meeting }) => {
    const { displayText, isLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);
    const isFavorited = savedMeetings.includes(meeting.id);
    
    return (
      <TouchableOpacity 
        style={styles.nextMeetingCard}
        onPress={() => handleMeetingPress(meeting)}
        activeOpacity={0.7}
      >
        <View style={[styles.nextMeetingImageContainer, meeting.plus_required ? styles.nextMeetingImageContainerPlus : isFavorited && styles.nextMeetingImageContainerFavorited]}>
          <ImageBackground
            source={require('../assets/images/image_1763325486152.png')}
            style={styles.nextMeetingImage}
            imageStyle={styles.nextMeetingImageStyle}
          >
            {meeting.plus_required ? (
              <MeetingPlusBadge />
            ) : isFavorited && (
              <View style={styles.meetingFavoriteBadge}>
                <View style={styles.meetingFavoriteBlock}>
                  <Svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <Path d="M8.49354 3.48565C8.30021 3.14698 8.20354 2.97765 8.07854 2.92098C7.96949 2.87154 7.84545 2.87154 7.7364 2.92098C7.6114 2.97765 7.51473 3.14698 7.3214 3.48565L6.11473 5.65365C6.06421 5.74804 6.03894 5.79523 6.00349 5.83223C5.97213 5.86498 5.93531 5.89189 5.89473 5.91182C5.84854 5.93432 5.79673 5.94565 5.69307 5.96832L3.25473 6.47632C2.86807 6.55632 2.67473 6.59565 2.57807 6.69432C2.49381 6.78037 2.44648 6.89588 2.44573 7.01632C2.44473 7.15432 2.5514 7.30232 2.76473 7.59765L4.45807 9.72632C4.53194 9.81874 4.56888 9.86494 4.59288 9.91606C4.61402 9.96156 4.6274 10.0101 4.6324 10.0599C4.63807 10.1159 4.6284 10.1739 4.60907 10.2899L4.29473 12.8213C4.24951 13.226 4.22691 13.4283 4.27807 13.5479C4.3225 13.6523 4.40703 13.7327 4.51307 13.7713C4.63473 13.8153 4.81807 13.7419 5.18473 13.5953L7.39807 12.6019C7.49673 12.5579 7.54607 12.5359 7.5974 12.5273C7.64273 12.5196 7.68921 12.5196 7.73454 12.5273C7.78588 12.5359 7.83521 12.5579 7.93388 12.6019L10.1472 13.5953C10.5139 13.7419 10.6972 13.8153 10.8189 13.7713C10.9249 13.7327 11.0094 13.6523 11.0539 13.5479C11.105 13.4283 11.0824 13.226 11.0372 12.8213L10.7229 10.2899C10.7035 10.1739 10.6939 10.1159 10.6995 10.0599C10.7046 10.0101 10.7179 9.96156 10.7391 9.91606C10.7631 9.86494 10.8 9.81874 10.8739 9.72632L12.5672 7.59765C12.7805 7.30232 12.8872 7.15432 12.8862 7.01632C12.8855 6.89588 12.8381 6.78037 12.7539 6.69432C12.6572 6.59565 12.4639 6.55632 12.0772 6.47632L9.63888 5.96832C9.53521 5.94565 9.4834 5.93432 9.43721 5.91182C9.39663 5.89189 9.35981 5.86498 9.32845 5.83223C9.293 5.79523 9.26773 5.74804 9.21721 5.65365L8.49354 3.48565Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="white"/>
                  </Svg>
                </View>
                <Svg style={{ position: 'absolute', top: 0, left: 24 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#C48000"/>
                </Svg>
                <Svg style={{ position: 'absolute', top: 24, left: 0 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0V8C0 1.29032 2.83871 0 8 0H0Z" fill="#C48000"/>
                </Svg>
              </View>
            )}
          </ImageBackground>
        </View>
        <View style={styles.nextMeetingContent}>
          <Text style={styles.nextMeetingTitle} numberOfLines={2}>{meeting.meeting_name}</Text>
          <View style={styles.nextMeetingTime}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M10.1016 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.28516 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 7.625H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 9.92773H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 7.625H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 9.92773H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 7.625H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 9.92773H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.nextMeetingTimeText}>
              {displayText}
            </Text>
          </View>
        </View>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path d="M6 3L11 8L6 13" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Svg>
      </TouchableOpacity>
    );
  };

  // Happening Now Card - Large card with header image
  const HappeningNowCard = ({ meeting, isSuggested = false, analyticsSource }: { meeting: Meeting; isSuggested?: boolean; analyticsSource?: 'para_voce' | 'explore' | 'salvos' }) => {
    const isSaved = savedMeetings.includes(meeting.id);
    const { displayText, isLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);

    return (
      <TouchableOpacity 
        style={styles.happeningNowCard}
        onPress={() => handleMeetingPress(meeting, analyticsSource)}
        activeOpacity={0.7}
      >
        <View style={[styles.happeningNowHeader, isSuggested && styles.happeningNowHeaderNoBorder]}>
          <ImageBackground
            source={require('../assets/images/image_1763325486152.png')}
            style={styles.happeningNowImage}
            imageStyle={styles.happeningNowImageStyle}
          >
            <LinearGradient
              colors={['rgba(255, 255, 255, 0)', '#FFFFFF']}
              locations={[0.25, 0.7]}
              style={styles.happeningNowGradient}
            >
              <View style={styles.happeningNowContent}>
                <Text style={styles.happeningNowTitle}>{meeting.meeting_name}</Text>
                <View style={styles.happeningNowStatusRow}>
                  <View style={styles.happeningNowStatusChip}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M10.1016 2.46143V4.44497" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M5.28516 2.46143V4.44497" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M5.01562 7.625H5.02229" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M5.01562 9.92773H5.02229" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M7.69141 7.625H7.69807" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M7.69141 9.92773H7.69807" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M10.3594 7.625H10.366" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M10.3594 9.92773H10.366" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                    <Text style={styles.happeningNowStatusText}>
                      {displayText}
                    </Text>
                  </View>
                </View>
              </View>
            </LinearGradient>
          </ImageBackground>
        </View>
        <View style={styles.happeningNowFooter}>
          <View style={styles.happeningNowAvatars}>
            {(meetingAttendees[meeting.id] || []).slice(0, 3).map((attendee, index) => (
              <View key={`avatar-${attendee.userId}-${index}`} style={{ marginLeft: index > 0 ? -8 : 0 }}>
                <Avatar
                  uri={attendee.profilePhotoUrl}
                  size={32}
                  outerBorderWidth={4}
                  borderColor="#FFFFFF"
                  useCache={false}
                />
              </View>
            ))}
            {/* Show placeholder avatars if less than 3 attendees */}
            {Array.from({ length: Math.max(0, 3 - (meetingAttendees[meeting.id] || []).length) }).map((_, index) => (
              <View key={`placeholder-${index}`} style={{ marginLeft: (meetingAttendees[meeting.id] || []).length + index > 0 ? -8 : 0 }}>
                <Avatar
                  size={32}
                  outerBorderWidth={4}
                  borderColor="#FFFFFF"
                />
              </View>
            ))}
          </View>
          <View style={styles.happeningNowActions}>
            {meeting.plus_required ? <ShapedSquareDiamond /> : null}
            <TouchableOpacity 
              style={[styles.happeningNowFavoriteButton, isSaved && styles.happeningNowFavoriteButtonActive]}
              onPress={(e) => {
                e.stopPropagation();
                toggleSavedMeeting(meeting.id);
              }}
            >
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M8.49354 3.48565C8.30021 3.14698 8.20354 2.97765 8.07854 2.92098C7.96949 2.87154 7.84545 2.87154 7.7364 2.92098C7.6114 2.97765 7.51473 3.14698 7.3214 3.48565L6.11473 5.65365C6.06421 5.74804 6.03894 5.79523 6.00349 5.83223C5.97213 5.86498 5.93531 5.89189 5.89473 5.91182C5.84854 5.93432 5.79673 5.94565 5.69307 5.96832L3.25473 6.47632C2.86807 6.55632 2.67473 6.59565 2.57807 6.69432C2.49381 6.78037 2.44648 6.89588 2.44573 7.01632C2.44473 7.15432 2.5514 7.30232 2.76473 7.59765L4.45807 9.72632C4.53194 9.81874 4.56888 9.86494 4.59288 9.91606C4.61402 9.96156 4.6274 10.0101 4.6324 10.0599C4.63807 10.1159 4.6284 10.1739 4.60907 10.2899L4.29473 12.8213C4.24951 13.226 4.22691 13.4283 4.27807 13.5479C4.3225 13.6523 4.40703 13.7327 4.51307 13.7713C4.63473 13.8153 4.81807 13.7419 5.18473 13.5953L7.39807 12.6019C7.49673 12.5579 7.54607 12.5359 7.5974 12.5273C7.64273 12.5196 7.68921 12.5196 7.73454 12.5273C7.78588 12.5359 7.83521 12.5579 7.93388 12.6019L10.1472 13.5953C10.5139 13.7419 10.6972 13.8153 10.8189 13.7713C10.9249 13.7327 11.0094 13.6523 11.0539 13.5479C11.105 13.4283 11.0824 13.226 11.0372 12.8213L10.7229 10.2899C10.7035 10.1739 10.6939 10.1159 10.6995 10.0599C10.7046 10.0101 10.7179 9.96156 10.7391 9.91606C10.7631 9.86494 10.8 9.81874 10.8739 9.72632L12.5672 7.59765C12.7805 7.30232 12.8872 7.15432 12.8862 7.01632C12.8855 6.89588 12.8381 6.78037 12.7539 6.69432C12.6572 6.59565 12.4639 6.55632 12.0772 6.47632L9.63888 5.96832C9.53521 5.94565 9.4834 5.93432 9.43721 5.91182C9.39663 5.89189 9.35981 5.86498 9.32845 5.83223C9.293 5.79523 9.26773 5.74804 9.21721 5.65365L8.49354 3.48565Z" stroke={isSaved ? "#C48000" : "#56554D"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill={isSaved ? "#C48000" : "none"}/>
              </Svg>
              <Text style={[styles.happeningNowFavoriteText, isSaved && styles.happeningNowFavoriteTextActive]}>
                {isSaved ? t('meetings.actions.favorited') : t('meetings.actions.favorite')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.happeningNowArrowButton}
              onPress={(e) => {
                e.stopPropagation();
                handleMeetingPress(meeting, analyticsSource);
              }}
            >
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <Path d="M6 12L10 8L6 4" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
              </Svg>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // Next Meetings Card - Small simple card
  const NextMeetingCard = ({ meeting, analyticsSource }: { meeting: Meeting; analyticsSource?: 'para_voce' | 'explore' | 'salvos' }) => {
    const { displayText, isLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);
    const isFavorited = savedMeetings.includes(meeting.id);
    
    return (
      <TouchableOpacity 
        style={styles.nextMeetingCard}
        onPress={() => handleMeetingPress(meeting, analyticsSource)}
        activeOpacity={0.7}
      >
        <View style={[styles.nextMeetingImageContainer, meeting.plus_required ? styles.nextMeetingImageContainerPlus : isFavorited && styles.nextMeetingImageContainerFavorited]}>
          <ImageBackground
            source={require('../assets/images/image_1763325486152.png')}
            style={styles.nextMeetingImage}
            imageStyle={styles.nextMeetingImageStyle}
          >
            {meeting.plus_required ? (
              <MeetingPlusBadge />
            ) : isFavorited && (
              <View style={styles.meetingFavoriteBadge}>
                <View style={styles.meetingFavoriteBlock}>
                  <Svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                    <Path d="M8.49354 3.48565C8.30021 3.14698 8.20354 2.97765 8.07854 2.92098C7.96949 2.87154 7.84545 2.87154 7.7364 2.92098C7.6114 2.97765 7.51473 3.14698 7.3214 3.48565L6.11473 5.65365C6.06421 5.74804 6.03894 5.79523 6.00349 5.83223C5.97213 5.86498 5.93531 5.89189 5.89473 5.91182C5.84854 5.93432 5.79673 5.94565 5.69307 5.96832L3.25473 6.47632C2.86807 6.55632 2.67473 6.59565 2.57807 6.69432C2.49381 6.78037 2.44648 6.89588 2.44573 7.01632C2.44473 7.15432 2.5514 7.30232 2.76473 7.59765L4.45807 9.72632C4.53194 9.81874 4.56888 9.86494 4.59288 9.91606C4.61402 9.96156 4.6274 10.0101 4.6324 10.0599C4.63807 10.1159 4.6284 10.1739 4.60907 10.2899L4.29473 12.8213C4.24951 13.226 4.22691 13.4283 4.27807 13.5479C4.3225 13.6523 4.40703 13.7327 4.51307 13.7713C4.63473 13.8153 4.81807 13.7419 5.18473 13.5953L7.39807 12.6019C7.49673 12.5579 7.54607 12.5359 7.5974 12.5273C7.64273 12.5196 7.68921 12.5196 7.73454 12.5273C7.78588 12.5359 7.83521 12.5579 7.93388 12.6019L10.1472 13.5953C10.5139 13.7419 10.6972 13.8153 10.8189 13.7713C10.9249 13.7327 11.0094 13.6523 11.0539 13.5479C11.105 13.4283 11.0824 13.226 11.0372 12.8213L10.7229 10.2899C10.7035 10.1739 10.6939 10.1159 10.6995 10.0599C10.7046 10.0101 10.7179 9.96156 10.7391 9.91606C10.7631 9.86494 10.8 9.81874 10.8739 9.72632L12.5672 7.59765C12.7805 7.30232 12.8872 7.15432 12.8862 7.01632C12.8855 6.89588 12.8381 6.78037 12.7539 6.69432C12.6572 6.59565 12.4639 6.55632 12.0772 6.47632L9.63888 5.96832C9.53521 5.94565 9.4834 5.93432 9.43721 5.91182C9.39663 5.89189 9.35981 5.86498 9.32845 5.83223C9.293 5.79523 9.26773 5.74804 9.21721 5.65365L8.49354 3.48565Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="white"/>
                  </Svg>
                </View>
                <Svg style={{ position: 'absolute', top: 0, left: 24 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#C48000"/>
                </Svg>
                <Svg style={{ position: 'absolute', top: 24, left: 0 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0V8C0 1.29032 2.83871 0 8 0H0Z" fill="#C48000"/>
                </Svg>
              </View>
            )}
          </ImageBackground>
        </View>
        <View style={styles.nextMeetingContent}>
          <Text style={styles.nextMeetingTitle} numberOfLines={2}>{meeting.meeting_name}</Text>
          <View style={styles.nextMeetingTime}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M10.1016 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.28516 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 7.625H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 9.92773H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 7.625H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 9.92773H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 7.625H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 9.92773H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.nextMeetingTimeText}>
              {displayText}
            </Text>
          </View>
        </View>
        <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <Path d="M10 8L14 12L10 16" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
        </Svg>
      </TouchableOpacity>
    );
  };

  const handleNotificationPress = () => {
    router.push('/notifications');
    markAllAsRead();
  };

  return (
    <SafeAreaView style={styles.container}>
      {/* Top Header */}
      <View style={styles.topHeader}>
        <TouchableOpacity 
          style={styles.avatarButton}
          onPress={() => router.push('/perfil')}
        >
          {hasPadrinhoPlus ? (
            <AvatarBlurRing>
              <Avatar
                uri={userProfile?.avatar_url ?? null}
                size={32}
                showInitials={true}
                {...(userProfile?.full_name && { initials: userProfile.full_name })}
                borderWidth={0}
              />
            </AvatarBlurRing>
          ) : (
            <Avatar
              uri={userProfile?.avatar_url ?? null}
              size={32}
              showInitials={true}
              {...(userProfile?.full_name && { initials: userProfile.full_name })}
              borderColor="#7E7D75"
              borderWidth={1}
            />
          )}
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{t('meetings.title')}</Text>
        <TouchableOpacity style={styles.notificationButton} onPress={handleNotificationPress}>
          <View style={styles.notificationIconContainer}>
            <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M12.5 17.8476C18.1392 17.8476 20.7481 17.1242 21 14.2205C21 11.3188 19.1812 11.5054 19.1812 7.94511C19.1812 5.16414 16.5452 2 12.5 2C8.45477 2 5.81885 5.16414 5.81885 7.94511C5.81885 11.5054 4 11.3188 4 14.2205C4.25295 17.1352 6.86177 17.8476 12.5 17.8476Z" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M14.8889 20.8572C13.5247 22.372 11.3967 22.3899 10.0195 20.8572" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
            {unreadCount > 0 && (
              <View style={styles.notificationBadge}>
                <Text style={styles.notificationBadgeText}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </Text>
              </View>
            )}
          </View>
        </TouchableOpacity>
      </View>

      {/* Tab Bar — fixed outside ScrollView */}
      <View style={styles.stickyTabsContainer}>
        {loading || savedMeetingsLoading ? (
          <>
            <View style={[styles.tabNavigation, { flex: 1 }]}>
              <View style={styles.skeletonTab} />
              <View style={styles.skeletonTab} />
            </View>
            <View style={[styles.tabSearchButton, { backgroundColor: '#E7E6E0' }]} />
          </>
        ) : (
          <>
            <View style={styles.tabNavigation}>
              <TouchableOpacity
                style={[styles.tabButton, activeTab === 'explore' && styles.activeTabButton]}
                onPress={() => handleMeetingTabPress('explore')}
                disabled={isSearchActive}
                activeOpacity={1}
              >
                <Text style={[styles.tabText, activeTab === 'explore' && styles.activeTabText]} numberOfLines={1}>
                  {t('meetings.tabs.explore')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.tabButton, activeTab === 'para_voce' && styles.activeTabButton]}
                onPress={() => handleMeetingTabPress('para_voce')}
                disabled={isSearchActive}
                activeOpacity={1}
              >
                <Text style={[styles.tabText, activeTab === 'para_voce' && styles.activeTabText]} numberOfLines={1}>
                  {t('meetings.tabs.para_voce')}
                </Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity
              style={styles.tabSearchButton}
              onPress={handleSearchPress}
              disabled={isSearchActive}
            >
              <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <Path fillRule="evenodd" clipRule="evenodd" d="M11.6324 3C16.2814 3 20.0499 6.76847 20.0499 11.4175C20.0499 16.0666 16.2814 19.836 11.6324 19.836C6.98331 19.836 3.21484 16.0666 3.21484 11.4175C3.21484 6.76847 6.98331 3 11.6324 3Z" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <Path d="M17.3594 17.584L20.7844 21.0002" stroke="#151515" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </Svg>
            </TouchableOpacity>
          </>
        )}
      </View>

      {/* Swipeable Content */}
      <SwipeableTabs
        ref={meetingsSwipeRef}
        activeIndex={meetingTabToIndex(activeTab)}
        onPageSelected={(i) => {
          const tab = MEETING_TABS[i];
          if (tab && tab !== activeTab) {
            Haptics.selectionAsync();
            changeMeetingTab(tab, 'swipe');
          }
        }}
      >
        {/* Page 0: Explore */}
        <ScrollView
          key="explore"
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} progressViewOffset={0} />
          }
        >
        {loading || savedMeetingsLoading ? (
          <View style={styles.tabContent}>
            <View style={styles.carouselSection}>
              <View style={[styles.skeletonTitle, { marginLeft: 24, marginBottom: 16 }]} />
              <View style={styles.carouselContainer}>
                <View style={styles.skeletonCarouselCard} />
              </View>
            </View>
            <View style={styles.groupChipsSection}>
              <View style={styles.groupChipsContainer}>
                <View style={styles.skeletonChip} />
                <View style={styles.skeletonChip} />
                <View style={styles.skeletonChip} />
              </View>
            </View>
            <View style={styles.meetingsListSection}>
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
            </View>
          </View>
        ) : (
          <View style={styles.tabContent}>
              {(() => {
                const explLive = carouselMeetings.filter(m => getMeetingDisplayTime(m.meeting_time, m.meeting_weekday, t).isLive);
                const explUpcoming = carouselMeetings.filter(m => !getMeetingDisplayTime(m.meeting_time, m.meeting_weekday, t).isLive);
                const explCarouselItems = explLive.length > 0 ? explLive : explUpcoming.slice(0, 3);
                const explTitle = explLive.length > 0 ? t('meetings.sections.happeningNow') : t('meetings.sections.suggested');
                const explIsSuggested = explLive.length === 0;
                // PRD §6 — the "group meetings" section is scoped to the
                // group that matches the current user's primary addiction
                // type. If no group matches (e.g. user hasn't picked an
                // addiction yet, or no live group exists for it), the
                // section is hidden entirely — we never fall back to a
                // random first group.
                const primaryAddiction = userProfile?.addiction_types?.[0]?.toLowerCase() ?? null;
                const matchedGroup = findGroupForAddiction(groupsWithMeetings, primaryAddiction);
                const firstGroup = matchedGroup;
                // Show the next 3 meetings for the matched group, sorted
                // by the upcoming session time. Prefer the server-enriched
                // `next_session.starts_at_iso`; meetings without one are
                // pushed to the end of the list deterministically.
                const groupMeetings = firstGroup
                  ? [...meetings.filter(m => m.group_id === firstGroup.id)]
                      .sort((a, b) => {
                        const ai = (a as any).next_session?.starts_at_iso || '9999';
                        const bi = (b as any).next_session?.starts_at_iso || '9999';
                        return ai < bi ? -1 : ai > bi ? 1 : 0;
                      })
                      .slice(0, 3)
                  : [];
                const listMeetings = filteredMeetings.filter(m => !getMeetingDisplayTime(m.meeting_time, m.meeting_weekday, t).isLive);
                return (
                  <>
                    {/* Global happening now carousel */}
                    {explCarouselItems.length > 0 && (
                      <View style={styles.carouselSection}>
                        <Text style={styles.carouselTitle}>{explTitle}</Text>
                        <ScrollView
                          horizontal
                          pagingEnabled
                          showsHorizontalScrollIndicator={false}
                          style={styles.carouselScroll}
                          contentContainerStyle={styles.carouselContainer}
                          decelerationRate="fast"
                          snapToInterval={345 + 8}
                          snapToAlignment="start"
                          onScroll={(e) => {
                            const idx = Math.round(e.nativeEvent.contentOffset.x / (345 + 8));
                            setCarouselIndex(idx);
                          }}
                          scrollEventThrottle={16}
                        >
                          {explCarouselItems.map((meeting) => (
                            <HappeningNowCard key={meeting.id} meeting={meeting} isSuggested={explIsSuggested} />
                          ))}
                        </ScrollView>
                        {explCarouselItems.length > 1 && (
                          <CarouselPagination count={explCarouselItems.length} activeIndex={carouselIndex} style={{ marginTop: 12 }} />
                        )}
                      </View>
                    )}

                    {/* Group Chips — one chip per Elo group. Post v2.0.0 the
                        Elo group IS the community (AA / NA / Família / …),
                        so no extra dedupe layer is needed. Elo groups must
                        route by their prefixed id (`elo:N`) since the server
                        only knows how to look those up — Supabase groups
                        route by slug as before. */}
                    {(() => {
                      const seen = new Set<string>();
                      const chips: Array<{ key: string; label: string; routeId: string }> = [];
                      for (const group of groupsWithMeetings) {
                        const isElo = String(group.id).startsWith('elo:');
                        const routeId = isElo ? group.id : (group.slug || group.id);
                        if (!routeId) continue;
                        const key = String(routeId);
                        if (seen.has(key)) continue;
                        seen.add(key);
                        chips.push({ key, label: group.name, routeId: String(routeId) });
                      }
                      if (chips.length === 0) return null;
                      // Render as a horizontal scroll with up to 2 rows so the
                      // section never grows taller than 2 lines. Fill column-by-
                      // column (chip 0 top, chip 1 bottom, chip 2 top, …) so
                      // reading order matches scroll direction.
                      const topRow = chips.filter((_, i) => i % 2 === 0);
                      const bottomRow = chips.filter((_, i) => i % 2 === 1);
                      const renderChip = (chip: { key: string; label: string; routeId: string }) => (
                        <TouchableOpacity
                          key={chip.key}
                          style={styles.groupChip}
                          onPress={() => router.push(`/group/${encodeURIComponent(chip.routeId)}`)}
                        >
                          <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <Path fillRule="evenodd" clipRule="evenodd" d="M4.24895 8.77344C3.00613 8.77437 1.99934 9.78257 2 11.0254C2.00067 12.2682 3.00853 13.2752 4.25135 13.2749C5.49417 13.2746 6.5015 12.267 6.5015 11.0242C6.50134 10.427 6.26391 9.8543 5.84146 9.43217C5.41901 9.01004 4.84615 8.7731 4.24895 8.77344Z" stroke="#3D3C38" strokeWidth="1.5" />
                            <Path fillRule="evenodd" clipRule="evenodd" d="M11.749 8.77344C10.5061 8.77437 9.4994 9.78257 9.5 11.0254C9.50067 12.2682 10.5085 13.2752 11.7514 13.2749C12.9942 13.2746 14.0015 12.267 14.0015 11.0242C14.0014 10.427 13.7639 9.8543 13.3415 9.43217C12.9191 9.01004 12.3462 8.7731 11.749 8.77344Z" stroke="#3D3C38" strokeWidth="1.5" />
                            <Path fillRule="evenodd" clipRule="evenodd" d="M7.99892 2.72656C6.75612 2.72756 5.74934 3.7357 5.75 4.97852C5.75067 6.22134 6.75852 7.2284 8.00132 7.22807C9.24419 7.22774 10.2515 6.22014 10.2515 4.97732C10.2513 4.38012 10.0139 3.80745 9.59145 3.38534C9.16899 2.96322 8.59612 2.72625 7.99892 2.72656Z" stroke="#3D3C38" strokeWidth="1.5" />
                          </Svg>
                          <Text style={styles.groupChipText} numberOfLines={1}>
                            {chip.label}
                          </Text>
                        </TouchableOpacity>
                      );
                      return (
                      <View style={styles.groupChipsSection}>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={styles.groupChipsScrollContent}
                        >
                          <View style={styles.groupChipsGrid}>
                            <View style={styles.groupChipsRow}>{topRow.map(renderChip)}</View>
                            {bottomRow.length > 0 && (
                              <View style={styles.groupChipsRow}>{bottomRow.map(renderChip)}</View>
                            )}
                          </View>
                        </ScrollView>
                      </View>
                      );
                    })()}

                    {/* Group next meetings section */}
                    {firstGroup && groupMeetings.length > 0 && (
                      <View style={styles.groupMeetingsSection}>
                        <View style={styles.groupMeetingsSectionHeader}>
                          <View style={styles.groupIconBadge}>
                            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <Path fillRule="evenodd" clipRule="evenodd" d="M4.24895 8.77344C3.00613 8.77437 1.99934 9.78257 2 11.0254C2.00067 12.2682 3.00853 13.2752 4.25135 13.2749C5.49417 13.2746 6.5015 12.267 6.5015 11.0242C6.50134 10.427 6.26391 9.8543 5.84146 9.43217C5.41901 9.01004 4.84615 8.7731 4.24895 8.77344Z" stroke="#151515" strokeWidth="1.5" />
                              <Path fillRule="evenodd" clipRule="evenodd" d="M11.749 8.77344C10.5061 8.77437 9.4994 9.78257 9.5 11.0254C9.50067 12.2682 10.5085 13.2752 11.7514 13.2749C12.9942 13.2746 14.0015 12.267 14.0015 11.0242C14.0014 10.427 13.7639 9.8543 13.3415 9.43217C12.9191 9.01004 12.3462 8.7731 11.749 8.77344Z" stroke="#151515" strokeWidth="1.5" />
                              <Path fillRule="evenodd" clipRule="evenodd" d="M7.99892 2.72656C6.75612 2.72756 5.74934 3.7357 5.75 4.97852C5.75067 6.22134 6.75852 7.2284 8.00132 7.22807C9.24419 7.22774 10.2515 6.22014 10.2515 4.97732C10.2513 4.38012 10.0139 3.80745 9.59145 3.38534C9.16899 2.96322 8.59612 2.72625 7.99892 2.72656Z" stroke="#151515" strokeWidth="1.5" />
                            </Svg>
                          </View>
                          <Text style={styles.groupMeetingsSectionTitle}>{firstGroup.name}</Text>
                          <TouchableOpacity
                            style={styles.groupArrowButton}
                            disabled={!firstGroup.slug && !String(firstGroup.id).startsWith('elo:')}
                            onPress={() => {
                              const isElo = String(firstGroup.id).startsWith('elo:');
                              const target = isElo ? firstGroup.id : firstGroup.slug;
                              if (target) router.push(`/group/${encodeURIComponent(String(target))}`);
                            }}
                          >
                            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
                            </Svg>
                          </TouchableOpacity>
                        </View>
                        <ScrollView
                          horizontal
                          showsHorizontalScrollIndicator={false}
                          contentContainerStyle={styles.groupMeetingsScrollContent}
                          snapToInterval={255 + 8}
                          decelerationRate="fast"
                          snapToAlignment="start"
                        >
                          {groupMeetings.map(meeting => {
                            const { displayText, isLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);
                            return (
                              <TouchableOpacity
                                key={meeting.id}
                                style={styles.groupMeetingCard}
                                activeOpacity={0.75}
                                onPress={() => handleMeetingPress(meeting)}
                              >
                                <View style={styles.groupMeetingCardHeader}>
                                  <Text style={styles.groupMeetingCardName} numberOfLines={2}>
                                    {meeting.meeting_name}
                                  </Text>
                                  <View style={styles.groupMeetingCardTimeRow}>
                                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                      <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0818 3.37598 10.4566 3.37598Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M5.01562 7.625H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M5.01562 9.92773H5.02229" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M7.69141 7.625H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M7.69141 9.92773H7.69807" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M10.3594 7.625H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M10.3594 9.92773H10.366" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M10.1016 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                      <Path d="M5.28516 2.46143V4.44497" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </Svg>
                                    <Text style={styles.groupMeetingCardTimeText}>
                                      {isLive ? t('meetings.sections.happeningNow') : displayText}
                                    </Text>
                                  </View>
                                </View>
                                <View style={styles.groupMeetingCardFooter}>
                                  <View style={styles.groupMeetingAvatars}>
                                    {(meetingAttendees[meeting.id] || []).slice(0, 3).map((attendee, idx) => (
                                      <View key={`${attendee.userId}-${idx}`} style={{ marginLeft: idx > 0 ? -8 : 0 }}>
                                        <Avatar uri={attendee.profilePhotoUrl} size={32} outerBorderWidth={4} borderColor="#F9F8F3" useCache={false} />
                                      </View>
                                    ))}
                                    {Array.from({ length: Math.max(0, 3 - (meetingAttendees[meeting.id] || []).length) }).map((_, idx) => (
                                      <View key={`gph-${idx}`} style={{ marginLeft: (meetingAttendees[meeting.id] || []).length + idx > 0 ? -8 : 0 }}>
                                        <Avatar size={32} outerBorderWidth={4} borderColor="#F9F8F3" />
                                      </View>
                                    ))}
                                  </View>
                                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                    {meeting.plus_required ? <ShapedSquareDiamond /> : null}
                                    <View style={styles.groupMeetingArrow}>
                                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <Path d="M5.83203 3.3335L10.4987 8.00016L5.83203 12.6668" stroke="#151515" strokeWidth="2" strokeLinecap="square"/>
                                      </Svg>
                                    </View>
                                  </View>
                                </View>
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>
                      </View>
                    )}

                    {/* Outros encontros header */}
                    <View style={styles.outrosMeetingsDivider} />
                    <View style={styles.outrosMeetingsHeader}>
                      <View style={styles.outrosMeetingsTitleRow}>
                        <View style={styles.outrosMeetingsIconBadge}>
                          <MegaphoneIcon size={16} color="#151515" />
                        </View>
                        <Text style={styles.outrosMeetingsTitleText}>{t('meetings.sections.otherMeetings')}</Text>
                      </View>
                    </View>

                    {/* PRD: filter chips were removed from the explore tab —
                        they now live inside the search overlay below the
                        input. The list below shows all meetings (sorted) by
                        default. */}

                    {/* Meetings list */}
                    {listMeetings.length > 0 ? (
                      <View style={styles.meetingsListSection}>
                        {listMeetings.map((meeting) => (
                          <NextMeetingCard key={meeting.id} meeting={meeting} />
                        ))}
                      </View>
                    ) : filteredMeetings.length === 0 && (
                      <View style={styles.emptyContainer}>
                        <Text style={styles.emptyText}>{t('meetings.empty.title')}</Text>
                      </View>
                    )}
                  </>
                );
              })()}
          </View>
        )}
        </ScrollView>

        {/* Page 1: Para Você */}
        <ScrollView
          key="para_voce"
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} progressViewOffset={0} />
          }
        >
        {loading || savedMeetingsLoading ? (
          <View style={styles.tabContent}>
            <View style={styles.carouselSection}>
              <View style={[styles.skeletonTitle, { marginLeft: 24, marginBottom: 16 }]} />
              <View style={styles.carouselContainer}>
                <View style={styles.skeletonCarouselCard} />
              </View>
            </View>
            <View style={styles.meetingsListSection}>
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
            </View>
          </View>
        ) : (
          <View style={styles.tabContent}>
              {(() => {
                const pvLive = paraVoceMeetings.filter(m => getMeetingDisplayTime(m.meeting_time, m.meeting_weekday, t).isLive);
                const pvUpcoming = paraVoceMeetings.filter(m => !getMeetingDisplayTime(m.meeting_time, m.meeting_weekday, t).isLive);
                const pvCarouselItems = pvLive.length > 0 ? pvLive : pvUpcoming.slice(0, 3);
                const pvTitle = pvLive.length > 0 ? t('meetings.sections.happeningNow') : t('meetings.sections.suggested');
                const pvIsSuggested = pvLive.length === 0;
                return (
                  <>
                    {pvCarouselItems.length > 0 && (
                      <View style={styles.carouselSection}>
                        <Text style={styles.carouselTitle}>{pvTitle}</Text>
                        <ScrollView
                          horizontal
                          pagingEnabled
                          showsHorizontalScrollIndicator={false}
                          style={styles.carouselScroll}
                          contentContainerStyle={styles.carouselContainer}
                          decelerationRate="fast"
                          snapToInterval={345 + 8}
                          snapToAlignment="start"
                          onScroll={(e) => {
                            const idx = Math.round(e.nativeEvent.contentOffset.x / (345 + 8));
                            setParaVoceCarouselIndex(idx);
                          }}
                          scrollEventThrottle={16}
                        >
                          {pvCarouselItems.map((meeting) => (
                            <HappeningNowCard key={meeting.id} meeting={meeting} isSuggested={pvIsSuggested} analyticsSource="para_voce" />
                          ))}
                        </ScrollView>
                        {pvCarouselItems.length > 1 && (
                          <CarouselPagination count={pvCarouselItems.length} activeIndex={paraVoceCarouselIndex} style={{ marginTop: 12 }} />
                        )}
                      </View>
                    )}
                    {paraVoceMeetings.length > 0 ? (
                      <View style={styles.meetingsListSection}>
                        {paraVoceMeetings.map((meeting) => (
                          <NextMeetingCard key={meeting.id} meeting={meeting} analyticsSource="para_voce" />
                        ))}
                      </View>
                    ) : (
                      <View style={styles.meetingsListSection}>
                        {meetings.slice(0, 6).map((meeting) => (
                          <NextMeetingCard key={meeting.id} meeting={meeting} analyticsSource="para_voce" />
                        ))}
                      </View>
                    )}
                  </>
                );
              })()}
          </View>
        )}
        </ScrollView>

        {/* Page 2: Salvos (PRD §7.3) */}
        <ScrollView
          key="salvos"
          style={styles.content}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#669AB7" colors={['#669AB7']} progressViewOffset={0} />
          }
        >
        {loading || savedMeetingsLoading ? (
          <View style={styles.tabContent}>
            <View style={styles.meetingsListSection}>
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
              <SkeletonMeetingCard />
            </View>
          </View>
        ) : (
          <View style={styles.tabContent}>
            {(() => {
              const salvosMeetings = meetings.filter(m => savedMeetings.includes(m.id));
              if (salvosMeetings.length === 0) {
                return (
                  <EmptyState
                    variant="top"
                    icon={
                      <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <Path d="M8.49354 3.48565C8.30021 3.14698 8.20354 2.97765 8.07854 2.92098C7.96949 2.87154 7.84545 2.87154 7.7364 2.92098C7.6114 2.97765 7.51473 3.14698 7.3214 3.48565L6.11473 5.65365C6.06421 5.74804 6.03894 5.79523 6.00349 5.83223C5.97213 5.86498 5.93531 5.89189 5.89473 5.91182C5.84854 5.93432 5.79673 5.94565 5.69307 5.96832L3.25473 6.47632C2.86807 6.55632 2.67473 6.59565 2.57807 6.69432C2.49381 6.78037 2.44648 6.89588 2.44573 7.01632C2.44473 7.15432 2.5514 7.30232 2.76473 7.59765L4.45807 9.72632C4.53194 9.81874 4.56888 9.86494 4.59288 9.91606C4.61402 9.96156 4.6274 10.0101 4.6324 10.0599C4.63807 10.1159 4.6284 10.1739 4.60907 10.2899L4.29473 12.8213C4.24951 13.226 4.22691 13.4283 4.27807 13.5479C4.3225 13.6523 4.40703 13.7327 4.51307 13.7713C4.63473 13.8153 4.81807 13.7419 5.18473 13.5953L7.39807 12.6019C7.49673 12.5579 7.54607 12.5359 7.5974 12.5273C7.64273 12.5196 7.68921 12.5196 7.73454 12.5273C7.78588 12.5359 7.83521 12.5579 7.93388 12.6019L10.1472 13.5953C10.5139 13.7419 10.6972 13.8153 10.8189 13.7713C10.9249 13.7327 11.0094 13.6523 11.0539 13.5479C11.105 13.4283 11.0824 13.226 11.0372 12.8213L10.7229 10.2899C10.7035 10.1739 10.6939 10.1159 10.6995 10.0599C10.7046 10.0101 10.7179 9.96156 10.7391 9.91606C10.7631 9.86494 10.8 9.81874 10.8739 9.72632L12.5672 7.59765C12.7805 7.30232 12.8872 7.15432 12.8862 7.01632C12.8855 6.89588 12.8381 6.78037 12.7539 6.69432C12.6572 6.59565 12.4639 6.55632 12.0772 6.47632L9.63888 5.96832C9.53521 5.94565 9.4834 5.93432 9.43721 5.91182C9.39663 5.89189 9.35981 5.86498 9.32845 5.83223C9.293 5.79523 9.26773 5.74804 9.21721 5.65365L8.49354 3.48565Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </Svg>
                    }
                    title={t('meetings.salvos.empty.title')}
                    subtitle={t('meetings.salvos.empty.subtitle')}
                    action={{
                      label: t('meetings.salvos.empty.cta'),
                      onPress: () => handleMeetingTabPress('explore'),
                    }}
                  />
                );
              }
              return (
                <View style={styles.meetingsListSection}>
                  {salvosMeetings.map((meeting) => (
                    <NextMeetingCard key={meeting.id} meeting={meeting} />
                  ))}
                </View>
              );
            })()}
          </View>
        )}
        </ScrollView>
      </SwipeableTabs>

      {/* Add Meeting Button for Admins - floating above navbar */}
      <FloatingActionButton isVisible={true} />

      {/* Time Filter Drawer - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: timeFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          ref={timeFilterRef}
          isVisible={timeFilterVisible}
          onClose={() => setTimeFilterVisible(false)}
          title={t('meetings.filters.time')}
          onApply={handleTimeFilterApply}
          onClear={handleTimeFilterClear}
          isApplyEnabled={tempSelectedTimes.length > 0}
          type="time"
        >
          <TimeFilterContent 
            onSelectionChange={setTempSelectedTimes} 
            currentSelection={tempSelectedTimes}
          />
        </FilterDrawer>
      </View>

      {/* Day Filter Drawer - wrapped in high z-index container */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: dayFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          ref={dayFilterRef}
          isVisible={dayFilterVisible}
          onClose={() => setDayFilterVisible(false)}
          title={t('meetings.filters.day')}
          onApply={handleDayFilterApply}
          onClear={handleDayFilterClear}
          isApplyEnabled={tempSelectedDays.length > 0}
          type="day"
        >
          <DayFilterContent 
            onSelectionChange={setTempSelectedDays}
            currentSelection={tempSelectedDays}
          />
        </FilterDrawer>
      </View>

      {/* Category Filter Drawer - wrapped in high z-index container so it
          overlays the SearchOverlay (zIndex 1000). */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: categoryFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          ref={categoryFilterRef}
          isVisible={categoryFilterVisible}
          onClose={() => setCategoryFilterVisible(false)}
          title={t('meetings.filters.category')}
          onApply={handleCategoryFilterApply}
          onClear={handleCategoryFilterClear}
          isApplyEnabled={tempSelectedCategories.length > 0}
          type="category"
        >
          <CategoryFilterContent
            onSelectionChange={setTempSelectedCategories}
            currentSelection={tempSelectedCategories}
            options={sessionCategoryOptions}
          />
        </FilterDrawer>
      </View>


      {/* Search Overlay */}
      <SearchOverlay
        visible={isSearchActive}
        query={searchQuery}
        onChangeQuery={setSearchQuery}
        onSubmit={handleSearchSubmit}
        onCancel={handleSearchCancel}
        placeholder={t('meetings.search.placeholder')}
        cancelLabel={t('meetings.search.cancelButton')}
        recentSearches={recentSearches}
        onRecentSearchPress={handleRecentSearchPress}
        filtersActive={selectedTimes.length > 0 || selectedDays.length > 0 || selectedCategories.length > 0}
        filterBar={
          <View style={styles.filterSection}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterContainer} keyboardShouldPersistTaps="always">
              <FilterChip
                icon={<ClockChipIcon />}
                label={selectedTimes.length > 0 ? selectedTimes[0] : t('meetings.filters.time')}
                active={selectedTimes.length > 0}
                onPress={handleTimeFilter}
                onClear={removeTimeFilter}
              />
              <FilterChip
                icon={<CalendarChipIcon />}
                label={selectedDays.length > 0 ? selectedDays[0] : t('meetings.filters.day')}
                active={selectedDays.length > 0}
                onPress={handleDayFilter}
                onClear={removeDayFilter}
              />
              <FilterChip
                icon={<CategoryChipIcon />}
                label={
                  selectedCategories.length > 0
                    ? (selectedCategories.length === 1
                        ? translateCategory(selectedCategories[0], t)
                        : `${t('meetings.filters.category')} (${selectedCategories.length})`)
                    : t('meetings.filters.category')
                }
                active={selectedCategories.length > 0}
                onPress={handleCategoryFilter}
                onClear={removeCategoryFilter}
              />
              {(selectedTimes.length > 0 || selectedDays.length > 0 || selectedCategories.length > 0) && (
                <TouchableOpacity style={styles.clearFiltersButton} onPress={clearAllFilters}>
                  <Text style={styles.clearFiltersText}>{t('meetings.filters.clear')}</Text>
                </TouchableOpacity>
              )}
            </ScrollView>
          </View>
        }
      >
        <View style={styles.searchResultsSection}>
          <Text style={styles.searchResultsTitle}>
            {filteredMeetings.length === 1
              ? t('meetings.search.results', { count: String(filteredMeetings.length) })
              : t('meetings.search.resultsPlural', { count: String(filteredMeetings.length) })}
          </Text>
          <View style={styles.searchResultsList}>
            {filteredMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))}
          </View>
        </View>
      </SearchOverlay>

      {/* Admin-only: Floating Test Notification Button */}
      {userProfile?.is_admin && (
        <TouchableOpacity
          style={[
            styles.testNotificationButton,
            testNotificationLoading && styles.testNotificationButtonLoading
          ]}
          onPress={sendTestNotification}
          disabled={testNotificationLoading}
          activeOpacity={0.8}
        >
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path
              d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <Path
              d="M13.73 21C13.5542 21.3031 13.3019 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6982 21.5547 10.4458 21.3031 10.27 21"
              stroke="#FFFFFF"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </Svg>
          <Text style={styles.testNotificationButtonText}>
            {testNotificationLoading ? 'Sending...' : 'Test Push'}
          </Text>
        </TouchableOpacity>
      )}

      {/* Toast Feedback */}
      <Toast
        visible={toastVisible}
        type={toastType}
        message={toastMessage}
        onHide={hideToast}
        duration={4000}
      />

      {/* Bottom Navigation */}
      <BillBottomNav />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
    position: 'relative',
  },
  topHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingLeft: 24,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 15,
    backgroundColor: '#F9F8F3',
  },
  avatarButton: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  notificationButton: {
    padding: 4,
  },
  notificationIconContainer: {
    position: 'relative',
  },
  notificationBadge: {
    position: 'absolute',
    top: -2,
    right: -2,
    backgroundColor: '#E53E3E',
    borderRadius: 10,
    minWidth: 16,
    height: 16,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  notificationBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  headerTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
  },
  tabContainer: {
    backgroundColor: '#F9F8F3',
  },
  carouselSection: {
    paddingTop: 16,
    paddingBottom: 24,
  },
  carouselTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    paddingHorizontal: 24,
    marginBottom: 12,
    lineHeight: 14,
  },
  groupChipsSection: {
    paddingBottom: 24,
  },
  groupChipsContainer: {
    paddingHorizontal: 24,
    gap: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 8,
  },
  groupChipsScrollContent: {
    paddingHorizontal: 24,
  },
  groupChipsGrid: {
    flexDirection: 'column',
    gap: 8,
  },
  groupChipsRow: {
    flexDirection: 'row',
    gap: 8,
  },
  stickyTabsContainer: {
    backgroundColor: '#F9F8F3',
    paddingHorizontal: 24,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  tabSearchButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  tabContent: {
    flex: 1,
  },
  meetingsListSection: {
    paddingHorizontal: 24,
    paddingBottom: 24,
    gap: 16,
  },
  skeletonTitle: {
    width: 180,
    height: 28,
    backgroundColor: '#E7E6E0',
    borderRadius: 8,
  },
  skeletonCarouselCard: {
    width: 345,
    height: 280,
    backgroundColor: '#E7E6E0',
    borderRadius: 16,
  },
  skeletonChip: {
    width: 100,
    height: 36,
    backgroundColor: '#E7E6E0',
    borderRadius: 17,
  },
  skeletonTab: {
    width: 70,
    height: 20,
    backgroundColor: '#E7E6E0',
    borderRadius: 4,
  },
  skeletonFilterChip: {
    width: 80,
    height: 36,
    backgroundColor: '#E7E6E0',
    borderRadius: 17,
  },
  tabNavigation: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    backgroundColor: '#F0EEE4',
    borderRadius: 32,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    height: 40,
    borderRadius: 24,
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  tabText: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#151515',
    lineHeight: 19.6,
  },
  activeTabButton: {
    backgroundColor: '#151515',
  },
  activeTabText: {
    fontFamily: 'Inter_500Medium',
    color: '#FFFFFF',
  },
  divider: {
    height: 1,
    backgroundColor: '#F0EEE4',
  },
  filterSection: {
    paddingVertical: 16,
    paddingLeft: 24,
  },
  filterContainer: {
    flexDirection: 'row',
    gap: 8,
    paddingRight: 24,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: 12,
    paddingRight: 12,
    paddingVertical: 8,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
    gap: 4,
  },
  filterText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },
  activeFilterChip: {
    backgroundColor: '#F0EEE4',
    borderColor: '#151515',
  },
  activeFilterText: {
    color: '#151515',
    fontFamily: 'Inter_600SemiBold',
  },
  clearFiltersButton: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  clearFiltersText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    lineHeight: 16.8,
  },
  groupMeetingsSection: {
    paddingTop: 16,
    paddingBottom: 24,
    gap: 12,
  },
  groupMeetingsSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 24,
  },
  groupIconBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  groupMeetingsSectionTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  groupArrowButton: {
    width: 32,
    height: 32,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  groupMeetingsScrollContent: {
    flexDirection: 'row',
    gap: 8,
    paddingHorizontal: 24,
  },
  groupMeetingCard: {
    width: 255,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
  },
  groupMeetingCardHeader: {
    padding: 24,
    paddingBottom: 16,
    minHeight: 98,
    justifyContent: 'flex-end',
    gap: 12,
  },
  groupMeetingCardName: {
    fontSize: 16,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 22.4,
  },
  groupMeetingCardTimeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  groupMeetingCardTimeText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 12,
  },
  groupMeetingCardFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  groupMeetingAvatars: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  groupMeetingArrow: {
    width: 32,
    height: 32,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  groupMeetingActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  groupMeetingGemButton: {
    width: 32,
    height: 32,
    borderRadius: 12,
    backgroundColor: '#F6F7F9',
    justifyContent: 'center',
    alignItems: 'center',
  },
  groupMeetingSaveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingLeft: 8,
    paddingRight: 12,
    paddingVertical: 8,
    borderRadius: 12,
    backgroundColor: '#F9F8F3',
    height: 32,
  },
  groupMeetingSaveText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  outrosMeetingsDivider: {
    height: 1,
    backgroundColor: '#F0EEE4',
    marginTop: 8,
  },
  outrosMeetingsHeader: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 12,
  },
  outrosMeetingsTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  outrosMeetingsIconBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  outrosMeetingsTitleText: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.6,
  },
  content: {
    paddingTop: 0,
    paddingBottom: 100,
  },
  meetingsList: {
    gap: 8,
    paddingBottom: 24,
  },
  meetingCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 24,
    gap: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeaderLeft: {
    flex: 1,
  },
  timeTag: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DAD8CF',
    borderRadius: 52,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignSelf: 'flex-start',
  },
  timeTagLive: {
    backgroundColor: '#669AB7', // Blue background for live meetings
  },
  timeText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  timeTextLive: {
    color: '#ffffff', // White text for live meetings
  },
  cardHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  meetingNotificationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#DAD8CF',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 6,
    minHeight: 36,
  },
  meetingNotificationButtonActive: {
    backgroundColor: '#DAD8CF',
  },
  notificationText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  externalButton: {
    backgroundColor: '#DAD8CF',
    borderRadius: 12,
    padding: 8,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    gap: 4,
  },
  meetingTitle: {
    fontSize: 20,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28,
  },
  meetingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  meetingType: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#56554D',
    lineHeight: 19.6,
  },
  meetingPlatform: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    lineHeight: 19.6,
  },
  // New 3-section layout styles
  firstSection: {
    gap: 16,
  },
  section: {
    paddingTop: 24,
    gap: 16,
  },
  lastSection: {
    paddingBottom: 24,
  },
  happeningNowSectionTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    lineHeight: 14,
  },
  sectionTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 24,
  },
  horizontalScroll: {
    gap: 8,
    paddingRight: 20,
  },
  carouselScroll: {
  },
  carouselContainer: {
    paddingLeft: 24,
    paddingRight: 8,
  },
  // Happening Now Card styles
  happeningNowCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    width: 345,
    overflow: 'hidden',
    marginRight: 8,
  },
  happeningNowHeader: {
    height: 150,
  },
  happeningNowHeaderNoBorder: {
    borderWidth: 0,
  },
  happeningNowImage: {
    width: '100%',
    height: '100%',
  },
  happeningNowImageStyle: {
    resizeMode: 'cover',
  },
  happeningNowGradient: {
    flex: 1,
    justifyContent: 'flex-end',
    padding: 24,
  },
  happeningNowContent: {
    gap: 12,
  },
  happeningNowTitle: {
    fontSize: 20,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28,
  },
  happeningNowStatusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  happeningNowStatusChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  happeningNowStatusText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#151515',
    lineHeight: 12,
  },
  happeningNowFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 0,
  },
  happeningNowAvatars: {
    flexDirection: 'row',
  },
  happeningNowActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  happeningNowFavoriteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    paddingVertical: 8,
    paddingLeft: 8,
    paddingRight: 12,
    height: 32,
  },
  happeningNowFavoriteButtonActive: {
    backgroundColor: '#F9F8F3',
  },
  happeningNowFavoriteText: {
    fontSize: 12,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#56554D',
    lineHeight: 16.8,
  },
  happeningNowFavoriteTextActive: {
    color: '#C48000',
  },
  happeningNowArrowButton: {
    backgroundColor: '#F9F8F3',
    borderRadius: 12,
    width: 32,
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  // Groups section styles
  groupsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  groupChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
  },
  groupChipText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },
  // Next Meetings Card styles
  nextMeetingsList: {
    gap: 16,
  },
  nextMeetingCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  nextMeetingImageContainer: {
    width: 64,
    height: 64,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  nextMeetingImageContainerFavorited: {
    borderColor: '#C48000',
  },
  nextMeetingImageContainerPlus: {
    borderColor: '#669AB7',
  },
  nextMeetingImage: {
    width: '100%',
    height: '100%',
  },
  nextMeetingImageStyle: {
    resizeMode: 'cover',
  },
  meetingFavoriteBadge: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 32,
    height: 32,
  },
  meetingFavoriteBlock: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 24,
    height: 24,
    backgroundColor: '#C48000',
    borderBottomRightRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  nextMeetingContent: {
    flex: 1,
    gap: 4,
  },
  nextMeetingTitle: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: 'bold',
    color: '#151515',
    lineHeight: 22.4,
  },
  nextMeetingTime: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  plusPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: '#FFE9CC',
    borderWidth: 1,
    borderColor: '#F4C98C',
  },
  plusPillText: {
    fontSize: 10,
    fontFamily: 'Inter_700Bold',
    color: '#C48000',
    lineHeight: 14,
    letterSpacing: 0.3,
  },
  nextMeetingTimeText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 12,
  },
  searchResultsSection: {
    paddingTop: 24,
  },
  searchResultsTitle: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#A19F97',
    marginBottom: 16,
  },
  searchResultsList: {
    gap: 24,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#A19F97',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
    paddingVertical: 80,
  },
  emptyContent: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 24,
    width: '100%',
  },
  illustrationContainer: {
    width: 280,
    height: 280,
  },
  illustrationImage: {
    width: '100%',
    height: '100%',
  },
  emptyTitle: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 28.8,
  },
  emptySubtitle: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    color: '#151515',
    textAlign: 'center',
    lineHeight: 19.6,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: 'Inter_500Medium',
    color: '#A19F97',
    textAlign: 'center',
  },
  testNotificationButton: {
    position: 'absolute',
    bottom: 112,
    left: 20,
    backgroundColor: '#015480',
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 30,
    gap: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  testNotificationButtonLoading: {
    backgroundColor: '#669AB7',
  },
  testNotificationButtonText: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '600',
    color: '#FFFFFF',
  },
});